import { paths } from '@/paths';
import type { AppNotification } from './notification-provider';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/** Prefer a UUID-shaped value; fall back to any non-empty id for legacy notifications. */
function pickNavId(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const value = String(candidate);
    if (isUuid(value)) return value;
  }

  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    return String(candidate);
  }

  return null;
}

/**
 * Maps a notification to the in-app route for the object it references.
 * Returns null when the notification has no navigable target.
 */
export function getNotificationLink(notification: AppNotification): string | null {
  const data = notification.data ?? {};
  const type = typeof data.type === 'string' ? data.type : '';
  const className = notification.type ?? '';

  const requestId = pickNavId(
    data.request_uuid,
    data.requestUuid,
    data.request_id,
    data.requestId
  );
  const reservationId = pickNavId(
    data.reservation_uuid,
    data.reservationUuid,
    data.reservation_id,
    data.reservationId
  );
  const announcementId = pickNavId(
    data.announcement_uuid,
    data.announcementUuid,
    data.announcement_id,
    data.announcementId
  );

  if (reservationId) {
    // Manager-facing approval requests live in community settings; resident-facing
    // approved/denied updates use the resident reservation route.
    if (type === 'reservation_requested') {
      return paths.settings.community.reservations.reservation(reservationId);
    }
    return paths.reservations.reservation(reservationId);
  }

  if (requestId && (type.includes('request') || className.includes('Request'))) {
    return paths.requests.request(requestId);
  }

  if (announcementId) {
    return paths.announcements.announcement(announcementId);
  }

  return null;
}
